import * as React from "react";
import { forwardRef, ReactNode, useId } from "react";
import { Box, ButtonElementProps, Column } from "@stenajs-webui/core";
import cx from "classnames";
import styles from "./MenuButton.module.css";
import { cssColor } from "@stenajs-webui/theme";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Icon } from "../../icon/Icon";
import { omit } from "lodash-es";
import { MenuButtonContent } from "./internal/MenuButtonContent";
import {
  stenaAngleDown,
  stenaAngleUp,
} from "../../../../icons/generated/ArrowIcons";
import { stenaCheck } from "../../../../icons/generated/CommonIcons";
import { InputSpinner } from "../../spinner/InputSpinner";

export type MenuButtonVariant = "standard" | "danger" | "success";

export type MenuButtonProps = MenuButtonLabelProps | MenuButtonRenderLabelProps;

export interface MenuButtonLabelProps extends MenuButtonCommonProps {
  label: string;
}

export interface MenuButtonRenderLabelProps extends MenuButtonCommonProps {
  renderLabel: () => ReactNode;
}

export interface MenuButtonCommonProps extends ButtonElementProps {
  expanded?: boolean;
  expandable?: boolean;
  selected?: boolean;
  leftIcon?: IconDefinition;
  left?: ReactNode;
  right?: ReactNode;
  rightIcon?: IconDefinition;
  variant?: MenuButtonVariant;
  children?: ReactNode;
  success?: boolean;
  loading?: boolean;
  accessibilityId?: string;
}

export const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(
  function (
    {
      accessibilityId,
      expanded,
      expandable,
      selected,
      className,
      leftIcon,
      rightIcon,
      children,
      disabled,
      variant = "standard",
      left,
      right,
      success = false,
      loading = false,
      ...rest
    },
    ref,
  ) {
    const menuButtonContentProps =
      "label" in rest
        ? { label: rest.label }
        : { renderLabel: rest.renderLabel };

    const buttonProps = omit(rest, ["label", "renderLabel"]);

    const generatedId = useId();
    const contentId = accessibilityId ?? generatedId;

    return (
      <Box gap={1}>
        <Box className={cx(styles.menuButton)} width={"100%"}>
          <button
            className={cx(
              styles.button,
              selected && styles.selected,
              disabled && styles.disabled,
              styles[variant],
              styles.hasLabel,
              className,
            )}
            disabled={disabled}
            ref={ref}
            aria-expanded={expandable ? expanded : undefined}
            aria-controls={expandable ? contentId : undefined}
            {...buttonProps}
          >
            <MenuButtonContent
              leftIcon={leftIcon}
              left={left}
              right={loading ? <InputSpinner /> : right}
              rightIcon={success ? stenaCheck : rightIcon}
              color={"var(--current-text-color)"}
              {...menuButtonContentProps}
            />
            {expandable && (
              <Icon
                icon={expanded ? stenaAngleUp : stenaAngleDown}
                size={18}
                color={cssColor("--lhds-color-blue-600")}
              />
            )}
          </button>
        </Box>

        {expanded && (
          <Column id={contentId} width={"100%"}>
            {children}
          </Column>
        )}
      </Box>
    );
  },
);
